import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  first,
  map,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  InvitationSocialLogin,
  LogoutSuccess,
  PartnerSocialLogin,
  RefreshTokenFailed,
  RefreshTokenSuccess,
  SocialLogin,
} from './+state/auth.actions';
import { AuthState, StoreInviteDetails } from './+state/auth.reducer';
import { getTokens } from './+state/auth.selectors';
import {
  SocialAccountLoginDetails,
  UserRegistrationDetails,
} from './../api/types/User';
import { LoggedInUser, LoginResponse, Tokens } from './auth';
import { LocationService } from '../public/location.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private isRefreshToken = false;
  private passwordAuthUsers = false;
  private passwordAuthSub = new Subject<boolean>();
  private loginEventListener: ((event: MessageEvent) => void) | null = null;
  private refreshTokenTimeout: ReturnType<typeof setTimeout> | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private store: Store<AuthState>,
    private injector: Injector,
  ) {
    this.onInit();
  }

  /**
   * Lazy on purpose: `LocationService` injects `HttpClient`, whose interceptor
   * chain is built from this service. Resolving it at call time, once the
   * app's injectors are up, keeps that out of a DI cycle (`NG0200`).
   */
  private navigateToLogin(): void {
    this.injector.get(LocationService).navigateToLogin();
  }

  private onInit(): void {
    this.listenToLoginEvents();
    this.keepTokenAlive();
  }

  private listenToLoginEvents() {
    // Store listener reference for cleanup
    this.loginEventListener = (event: MessageEvent) => {
      const { data } = event;
      if (data?.mode === 'admin-login') {
        this.adminLogin(data);
      }

      if (data?.mode?.includes('registration')) {
        this.adminRegistration(data);
      }
    };

    window.addEventListener('message', this.loginEventListener, false);
  }

  private keepTokenAlive() {
    const isAdmin = window.location.href.includes('admin');
    if (!isAdmin) {
      return;
    }

    this.store
      .select(getTokens)
      .pipe(
        filter((tokens) => !!(tokens?.jwt && tokens?.refreshToken)),
        takeUntil(this.destroy$),
      )
      .subscribe((tokens) => {
        // Clear any existing timeout
        if (this.refreshTokenTimeout !== null) {
          clearTimeout(this.refreshTokenTimeout);
        }

        const { jwt, refreshToken } = tokens;
        const jwtHelper = new JwtHelperService();
        const expTime = jwtHelper.getTokenExpirationDate(jwt).getTime();
        const currentTime = Date.now();
        const exp = expTime - currentTime - 30000;
        const refreshTime = exp > 100 ? exp : 100;

        // Track timeout for cleanup
        this.refreshTokenTimeout = setTimeout(() => {
          this.refreshToken(refreshToken).subscribe();
        }, refreshTime);
      });
  }

  adminLogin(data: unknown): void {
    if (
      typeof data === 'object' &&
      data !== null &&
      'email' in data &&
      typeof data.email === 'string'
    ) {
      const typedData = data as Record<string, unknown>;
      const socialAccountDetails: SocialAccountLoginDetails = {
        provider: typedData.provider as string,
        accessToken: (typedData.idToken ??
          typedData.authToken ??
          typedData.accessToken) as string,
        email: typedData.email as string,
        firstName: typedData.firstName as string,
        lastName: typedData.lastName as string,
      };

      this.store.dispatch(
        new SocialLogin(socialAccountDetails, {
          src: 'AuthService',
          description: 'Admin login event',
        }),
      );
    }
  }

  adminRegistration(data: unknown): void {
    if (
      typeof data === 'object' &&
      data !== null &&
      'email' in data &&
      typeof data.email === 'string'
    ) {
      const typedData = data as Record<string, unknown>;
      const userRegistrationDetails: UserRegistrationDetails = {
        email: typedData.email as string,
        firstName: typedData.firstName as string,
        lastName: typedData.lastName as string,
        password: undefined,
        social: {
          provider: typedData.provider as string,
          accessToken: (typedData.idToken ??
            typedData.authToken ??
            typedData.accessToken) as string,
          appleCode: typedData.appleCode as string,
          email: typedData.email as string,
          firstName: typedData.firstName as string,
          lastName: typedData.lastName as string,
        },
      };

      switch (typedData.mode as string) {
        case 'admin-registration':
          this.adminSocialRegistration(userRegistrationDetails);
          break;
        case 'partner-registration':
          this.partnerRegistration(userRegistrationDetails);
          break;
        case 'invite-registration':
          this.invitationRegistration(userRegistrationDetails);
          break;
      }
    }
  }

  private adminSocialRegistration(userSubmit: UserRegistrationDetails) {
    this.store.dispatch(new SocialLogin(userSubmit.social));
  }

  private partnerRegistration(
    userRegistrationDetails: UserRegistrationDetails,
  ) {
    this.store.dispatch(new PartnerSocialLogin(userRegistrationDetails.social));
  }

  private invitationRegistration(invUser: UserRegistrationDetails) {
    const token = this.route.snapshot.queryParams.token;
    const store = this.route.snapshot.queryParams.store;
    this.store.dispatch(
      new InvitationSocialLogin(invUser.social, token, store),
    );
  }

  login(username: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('/api/v1/auth/login', {
      username,
      password,
    });
  }

  socialLogin(
    accessToken: string,
    authProvider: string,
    appleCode: string,
  ): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`/api/v1/auth/login/${authProvider}`, {
      accessToken,
      appleCode,
    });
  }

  registerV2(
    data: UserRegistrationDetails,
    captchaToken: string,
  ): Observable<{}> {
    return this.http.post<{}>('/api/v2/auth/register', {
      ...data,
      captchaToken,
    });
  }

  registerPartnerV2(
    data: UserRegistrationDetails,
    captchaToken: string,
  ): Observable<{}> {
    return this.http.post<{}>('/api/v2/auth/register/partner', {
      ...data,
      captchaToken,
    });
  }

  registerByInvitation(
    data: UserRegistrationDetails,
    token: string,
  ): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('/api/v1/auth/register/invitation', {
      ...data,
      token,
    });
  }

  verify(email: string): Observable<{}> {
    return this.http.post<{}>('/api/v1/auth/email/verify', email);
  }

  activate(token: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>('/api/v1/auth/email/activate', token);
  }

  activatePartner(token: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      '/api/v1/auth/partner/activate',
      token,
    );
  }

  resetPassword(email: string): Observable<{}> {
    return this.http.post<{}>('/api/v1/auth/password/reset', email);
  }

  verifyToken(token: string, type: string): Observable<string> {
    return this.http.get<string>(
      `/api/v1/auth/token/verify?token=${token}&type=${type}`,
      { responseType: 'text' as 'json' },
    );
  }

  updatePassword(password: string, token: string): Observable<{}> {
    return this.http.post<{}>('/api/v1/auth/password/update', {
      password,
      token,
    });
  }

  logout() {
    return this.http.post<Record<string, never>>('/api/v1/auth/logout', {});
  }

  isLoggedIn(): Observable<boolean> {
    return this.store.select(getTokens).pipe(
      first(),
      map((tokens) => !!tokens.jwt),
    );
  }

  refreshToken(refreshToken: string) {
    this.isRefreshingToken = true;
    const event = { src: 'AuthService', description: 'Refresh token' };
    return this.http
      .post<Tokens>('/api/v1/auth/token/refresh', refreshToken)
      .pipe(
        tap((token) => {
          if (token?.refreshToken) {
            this.store.dispatch(new RefreshTokenSuccess(token, event));
          } else {
            this.store.dispatch(new RefreshTokenFailed(event));
          }
        }),
        catchError((error) => {
          if (
            !error?.status ||
            error.status === 401 ||
            (error.status >= 500 && error.status < 600) ||
            error.status === 0
          ) {
            this.store.dispatch(
              new LogoutSuccess({
                src: 'AuthService',
                description: '401 error from refresh token',
              }),
            );
            this.navigateToLogin();
          } else {
            throw error;
          }
          return of({});
        }),
        finalize(() => (this.isRefreshingToken = false)),
      );
  }

  get isRefreshingToken() {
    return this.isRefreshToken;
  }

  set isRefreshingToken(isrefreshToken: boolean) {
    this.isRefreshToken = isrefreshToken;
  }

  get usersPasswordAuth() {
    return this.passwordAuthUsers;
  }

  set usersPasswordAuth(passAuth: boolean) {
    this.passwordAuthUsers = passAuth;
    this.passwordAuthSub.next(this.passwordAuthUsers);
  }

  getPasswordAuth(): Observable<any> {
    return this.passwordAuthSub.asObservable();
  }

  fetchLoggedInUser(): Observable<LoggedInUser> {
    return this.http.get<LoggedInUser>('/api/v1/auth/currentuser');
  }

  getLocationInfo(): Observable<any> {
    return this.http.get<any>('https://freeipapi.com/api/json');
  }

  validateStoreInviteToken(token: string): Observable<StoreInviteDetails> {
    const request = {
      token: token,
    };
    return this.http.post<StoreInviteDetails>(
      `/api/v1/auth/validate-invited-user-by-token`,
      request,
    );
  }

  ngOnDestroy() {
    // Remove window event listener
    if (this.loginEventListener !== null) {
      window.removeEventListener('message', this.loginEventListener, false);
      this.loginEventListener = null;
    }

    // Clear timeout
    if (this.refreshTokenTimeout !== null) {
      clearTimeout(this.refreshTokenTimeout);
      this.refreshTokenTimeout = null;
    }

    // Complete subjects
    this.destroy$.next();
    this.destroy$.complete();
    this.passwordAuthSub.complete();
  }
}
